// ConnectiveBadge.tsx — AND / OR / AND NOT toggle between swim lanes (PRI-291)
// Spec §7: pill-shaped badge centred on a thin divider line

import type { ConnectiveValue } from './compiled-query-types'

// Light-mode colour values (V2 components use forced light mode)
const COLORS: Record<ConnectiveValue, { bg: string; text: string }> = {
  and:     { bg: 'rgba(27,79,224,0.08)',  text: '#1B4FE0' },
  or:      { bg: 'rgba(108,40,200,0.08)', text: '#6D28C8' },
  and_not: { bg: 'rgba(220,38,38,0.08)',  text: '#DC2626' },
}

const LABELS: Record<ConnectiveValue, string> = {
  and:     'AND',
  or:      'OR',
  and_not: 'AND NOT',
}

const CYCLE: Record<ConnectiveValue, ConnectiveValue> = {
  and:     'or',
  or:      'and_not',
  and_not: 'and',
}

interface ConnectiveBadgeProps {
  value: ConnectiveValue
  editMode: boolean
  onChange?: (v: ConnectiveValue) => void
}

export function ConnectiveBadge({ value, editMode, onChange }: ConnectiveBadgeProps) {
  const colors = COLORS[value]
  const label = LABELS[value]

  function handleClick() {
    if (!editMode || !onChange) return
    onChange(CYCLE[value])
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 20,
        margin: '0 10px',
      }}
    >
      {/* Thin divider line */}
      <div style={{
        position: 'absolute',
        left: 0, right: 0, top: '50%',
        height: 1,
        background: '#D4D0C8',
        transform: 'translateY(-50%)',
      }} />
      {/* Badge pill */}
      <div
        role={editMode ? 'button' : undefined}
        aria-label={`Connective: ${label}`}
        onClick={handleClick}
        style={{
          position: 'relative',
          zIndex: 1,
          background: colors.bg,
          color: colors.text,
          padding: '2px 7px',
          borderRadius: 999,
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 9,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          cursor: editMode ? 'pointer' : 'default',
          transition: 'background 150ms ease, color 150ms ease',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
    </div>
  )
}
